// ============================================
// PROCESSING REDUCER - Per-file state tracking for import pipeline
// ============================================
// Tracks each file through: queued → processing → done/error
// Used by ProcessingView to show real-time progress

import { resolveDocumentClassification } from '../../../utils/document-classification';
import { documentSourceIdentity } from '../../../utils/merge-docs-by-identity';
import {
    documentImportOutcome,
    outcomeMessage,
    outcomeStatus,
    summariseFloor,
    videoHoldsTranscript,
    videoRowDetail,
    videoRowOutcome,
    type ImportOutcome,
} from '../../../services/stage1-import-outcome';

export interface ProcessingFile {
    id: string;
    name: string;
    size: number;
    category: string;
    status: string;
    message?: string;
    label?: string | null;
    results?: any;
    progress?: number;
    /**
     * How the file ended (stage1-import-outcome.ts). Absent on a row still in flight and on rows from before outcomes existed, which read
     * as a plain success. The status badge and the counts follow status; the outcome says WHICH kind of done or error it is.
     */
    outcome?: ImportOutcome;
    /**
     * An imported file's row (INIT_FILES): the file itself and its physical-source identity, so the Files tab can
     * hold back the document or video this row is still reading (SPEC 2026-10-21 B9). In memory only.
     */
    sourceIdentity?: string;
    sourceFile?: unknown;
    [key: string]: any;
}

/** How the last import ended, counted once every one of its files settled (summariseFloor over its rows). */
export type ProcessingRunSummary = ReturnType<typeof summariseFloor>;

export interface ProcessingFeedItem {
    id: string;
    timestamp: number;
    icon?: any;
    text?: string;
    type?: string;
    [key: string]: any;
}

export interface ProcessingState {
    files: ProcessingFile[];
    feed: ProcessingFeedItem[];
    totalFiles: number;
    processedFiles: number;
    errors: number;
    /**
     * The last import's outcome counts, set when its last file settles. It outlives RECONCILE_FROM_ARTIFACTS, which
     * replaces the rows with the saved files (video frames included), so the Files tab can still say "10 files read".
     * A new import, CLEAR and a project load drop it.
     */
    lastRun?: ProcessingRunSummary | null;
}


export const FILE_STATUS = {
    QUEUED: 'queued',
    PROCESSING: 'processing',
    DONE: 'done',
    ERROR: 'error'
};

export const ACTIONS = {
    INIT_FILES: 'INIT_FILES',
    UPDATE_FILE: 'UPDATE_FILE',
    ADD_FILE: 'ADD_FILE',
    ADD_FEED: 'ADD_FEED',
    CLEAR: 'CLEAR',
    // 2026-05-17 (Peter): rehydrate the Sorting Floor from a project loaded
    // out of Project History. The original procState only exists in-session
    // (after a fresh import via INIT_FILES); re-opening the project showed
    // an empty intake area because procState.totalFiles was 0. This action
    // reconstructs synthetic file entries from the persisted documents/photos/
    // videos arrays so the Sorting Floor reappears with everything marked DONE.
    REHYDRATE_FROM_LOADED: 'REHYDRATE_FROM_LOADED',
    // Fresh processing temporarily tracks source files, virtual note rows and
    // transcripts. Once processing settles, replace that transport projection
    // with the same durable documents/photos/videos projection used on reload.
    RECONCILE_FROM_ARTIFACTS: 'RECONCILE_FROM_ARTIFACTS',
    // The operator cancelled this import (or it was stopped while it still owned the project). Rows of THIS import that never reached a
    // terminal state are settled as cancelled, so the floor stops counting them as busy. Rows already DONE or ERROR are left alone, and a
    // run that no longer owns the project does not dispatch it at all (stale work publishes nothing).
    SETTLE_CANCELLED: 'SETTLE_CANCELLED',
    // The import failed outside every per-kind handler. Like SETTLE_CANCELLED it touches only the rows still in flight, so a
    // file that already finished keeps its outcome; the rest are failed, with the payload's message.
    SETTLE_UNFINISHED: 'SETTLE_UNFINISHED',
    // A clip that holds a transcript (its video row, or the project's transcript rows) while its floor row is an error: evidence
    // preparation before the Summary transcribed a clip the import could not, or the floor was built before the transcript rows
    // arrived. Its words are in the notes, so its row says transcribed (2026-10-28, RBB/BRC (1026) - UNIT 17).
    SETTLE_TRANSCRIBED_VIDEOS: 'SETTLE_TRANSCRIBED_VIDEOS',
};

/**
 * The video a Sorting Floor row stands for. A saved file's row carries its video; an import row is the same File. Only an
 * import row with no File falls back to the one video of that name: until an imported clip's own entry lands, a video of its
 * name is an older one already on the job.
 */
export function floorRowVideo(videos: any[], row: any): any | null {
    const savedId = row?.results?.id;
    if (savedId != null) return videos.find(video => video?.id === savedId) || null;
    if (row?.sourceFile) return videos.find(video => video?.file && video.file === row.sourceFile) || null;
    const named = videos.filter(video => video?.name === row?.name);
    return named.length === 1 ? named[0] : null;
}

const SORTING_FLOOR_DOCUMENT_LANES = new Set(['rfq', 'plan', 'assessor_notes', 'report', 'estimator_notes']);

/**
 * Resolve the one canonical label used by both a fresh Stage 1 import and a
 * loaded-project Sorting Floor replay. Keeping this at the reducer boundary
 * prevents the fresh path from preferring a stale `aiClassification` while
 * the reload path independently prefers trusted content provenance.
 */
export function resolveSortingFloorDocumentLabel(document: any) {
    const classification = resolveDocumentClassification(document).classification;
    return SORTING_FLOOR_DOCUMENT_LANES.has(classification as string) ? classification : null;
}

export function buildSortingFloorArtifactFiles(payload: any = {}, message: string = 'Restored from saved project'): ProcessingFile[] {
    const { documents = [], photos = [], videos = [], videoTranscripts = [] } = payload || {};
    // Status and wording come from what the SAVED record says happened (5A / C02): a PDF whose text could not be read is an error row that
    // keeps its file, a partly read one is a done row that says so, a video kept without transcription is a done row that says so. The
    // plain successes keep the caller's message ('Restored from saved project', 'Processed import artifact'). A clip the project's
    // transcript rows hold is transcribed, whatever its video row says.
    const settled = (outcome: ImportOutcome, kind: 'document' | 'video' | 'other', detail: string | null = null) => ({
        status: outcomeStatus(outcome) === 'error' ? FILE_STATUS.ERROR : FILE_STATUS.DONE,
        message: outcome === 'extracted' || outcome === 'transcribed' ? message : outcomeMessage(outcome, detail, kind),
        outcome,
    });
    const docFiles = documents.map((d: any, i: number) => ({
        id: `rehydrated_doc_${i}_${d?.name || d?.id || 'doc'}`,
        name: d?.name || d?.originalName || `Document ${i + 1}`,
        size: d?.size || 0,
        category: 'document',
        ...settled(documentImportOutcome(d), 'document'),
        label: resolveSortingFloorDocumentLabel(d),
        results: d,
        progress: 100,
    }));
    const photoFiles = photos.map((p: any, i: number) => ({
        id: `rehydrated_photo_${i}_${p?.id || p?.blobId || 'photo'}`,
        name: p?.name || p?.originalName || `Photo ${i + 1}`,
        size: p?.size || (p?.sizeKB ? p.sizeKB * 1024 : 0),
        category: 'image',
        ...settled('extracted', 'other'),
        label: null,
        results: p,
        progress: 100,
    }));
    const videoFiles = videos.map((v: any, i: number) => ({
        id: `rehydrated_video_${i}_${v?.id || v?.videoBlobId || 'video'}`,
        name: v?.name || v?.originalName || `Video ${i + 1}`,
        size: v?.size || 0,
        category: 'video',
        ...settled(videoRowOutcome(v, videoTranscripts), 'video', videoRowDetail(v)),
        label: null,
        results: v,
        progress: 100,
    }));
    return [...docFiles, ...photoFiles, ...videoFiles];
}

/** A row's outcome for comparison: rows from before outcomes existed (or still in flight) read as the plain success of their kind. */
function rowOutcome(file: any): string {
    return file?.outcome || (file?.category === 'video' ? 'transcribed' : 'extracted');
}

function artifactProjectionKey(file: any): string {
    return `${file?.category || ''}\u001f${file?.name || ''}\u001f${file?.label || ''}\u001f${rowOutcome(file)}`;
}

export function isSortingFloorArtifactProjection(state: any, payload: any): boolean {
    const current = Array.isArray(state?.files) ? state.files : [];
    const expected = buildSortingFloorArtifactFiles(payload);
    const expectedErrors = expected.filter(file => file.status === FILE_STATUS.ERROR).length;
    return current.length === expected.length
        && Number(state?.totalFiles || 0) === expected.length
        && Number(state?.processedFiles || 0) === expected.length
        && Number(state?.errors || 0) === expectedErrors
        && current.every((file: any, index: number) => (
            file?.status === expected[index]?.status
            && artifactProjectionKey(file) === artifactProjectionKey(expected[index])
        ));
}

const initialState: ProcessingState = {
    files: [],        // { id, name, size, category, status, message, label, results, progress }
    feed: [],         // { id, timestamp, icon, text, type }
    totalFiles: 0,
    processedFiles: 0,
    errors: 0,
    lastRun: null,
};

const isSettled = (file: ProcessingFile) => file.status === FILE_STATUS.DONE || file.status === FILE_STATUS.ERROR;

/** A row INIT_FILES made for an imported file (`file_<i>_<name>`), not a transcript row or a saved file. */
export function isImportRow(file: unknown): boolean {
    const id = (file as ProcessingFile | null)?.id;
    return typeof id === 'string' && id.startsWith('file_');
}

/** The state with lastRun counted once every row of the current import has settled; otherwise unchanged. */
function withLastRun(state: ProcessingState): ProcessingState {
    const rows = state.files.filter(isImportRow);
    if (rows.length === 0 || !rows.every(isSettled)) return state;
    return { ...state, lastRun: summariseFloor(rows) };
}

export function processingReducer(state: ProcessingState = initialState, action: any): ProcessingState {
    switch (action.type) {
        case ACTIONS.INIT_FILES: {
            const files = action.payload.map((file: any, i: number) => ({
                id: `file_${i}_${file.name}`,
                name: file.name,
                size: file.size,
                category: action.categories?.[i] || 'unknown',
                status: FILE_STATUS.QUEUED,
                message: '',
                label: null,
                results: null,
                progress: 0,  // 0-100 for long-running operations like transcription
                sourceIdentity: documentSourceIdentity(file),
                sourceFile: file,
            }));
            return {
                ...state,
                files,
                feed: [],
                totalFiles: files.length,
                processedFiles: 0,
                errors: 0,
                lastRun: null,
            };
        }

        case ACTIONS.UPDATE_FILE: {
            const { fileId, updates } = action.payload;
            const files = state.files.map(f =>
                f.id === fileId ? { ...f, ...updates } : f
            );
            const processedFiles = files.filter(f => f.status === FILE_STATUS.DONE || f.status === FILE_STATUS.ERROR).length;
            const errors = files.filter(f => f.status === FILE_STATUS.ERROR).length;
            return withLastRun({ ...state, files, processedFiles, errors });
        }

        case ACTIONS.ADD_FILE: {
            const newFile = action.payload;
            const files = [...state.files, newFile];
            const processedFiles = files.filter(f => f.status === FILE_STATUS.DONE || f.status === FILE_STATUS.ERROR).length;
            return withLastRun({ ...state, files, totalFiles: files.length, processedFiles });
        }

        case ACTIONS.ADD_FEED: {
            const feedItem = {
                id: `feed_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
                timestamp: Date.now(),
                ...action.payload
            };
            return {
                ...state,
                feed: [...state.feed, feedItem]
            };
        }

        case ACTIONS.CLEAR:
            return initialState;

        case ACTIONS.REHYDRATE_FROM_LOADED:
        case ACTIONS.RECONCILE_FROM_ARTIFACTS: {
            // Project the durable artefact arrays for either a completed fresh
            // import or a project loaded from history. Every entry is settled
            // (DONE, or ERROR where the saved record says the file could not be
            // read or transcribed): transport-only source-note/transcript rows
            // are intentionally not durable Sorting Floor files, while extracted
            // video frames in the photos array are.
            //
            // Routing (consumed by ProcessingView's fileLaneKey):
            //   - documents → category='document'; label = doc.class when it
            //     matches one of the known lanes (rfq | plan |
            //     assessor_notes | report), otherwise null → 'other'.
            //   - photos    → category='image' → routes to the 'photo' lane.
            //   - videos    → category='video' → routes to the 'video' lane.
            const isFreshReconciliation = action.type === ACTIONS.RECONCILE_FROM_ARTIFACTS;
            // ERROR rows count as processed, but they are still unresolved
            // operator evidence. Never replace them with the successful-only
            // artefact arrays or falsely reset the visible error count to zero.
            if (isFreshReconciliation && Number(state?.errors || 0) > 0) return state;
            const files = buildSortingFloorArtifactFiles(
                action.payload,
                isFreshReconciliation ? 'Processed import artifact' : 'Restored from saved project',
            );
            return {
                ...state,
                files,
                feed: isFreshReconciliation ? state.feed : [],
                totalFiles: files.length,
                processedFiles: files.length,
                // Rows the saved record says failed are ERROR rows again, so a reload shows the same error count it showed live.
                errors: files.filter(file => file.status === FILE_STATUS.ERROR).length,
                // The import that just settled keeps its count; a project loaded from history had no import here.
                lastRun: isFreshReconciliation ? state.lastRun ?? null : null,
            };
        }

        case ACTIONS.SETTLE_CANCELLED:
        case ACTIONS.SETTLE_UNFINISHED: {
            const cancelled = action.type === ACTIONS.SETTLE_CANCELLED;
            const outcome: ImportOutcome = cancelled ? 'cancelled' : 'failed';
            const message = cancelled ? outcomeMessage('cancelled') : outcomeMessage('failed', action.payload?.message);
            const ids = new Set<string>(Array.isArray(action.payload?.fileIds) ? action.payload.fileIds : []);
            const files = state.files.map(f => (
                ids.has(f.id) && (f.status === FILE_STATUS.QUEUED || f.status === FILE_STATUS.PROCESSING)
                    ? { ...f, status: FILE_STATUS.ERROR, message, outcome }
                    : f
            ));
            const processedFiles = files.filter(f => f.status === FILE_STATUS.DONE || f.status === FILE_STATUS.ERROR).length;
            const errors = files.filter(f => f.status === FILE_STATUS.ERROR).length;
            return withLastRun({ ...state, files, processedFiles, errors });
        }

        case ACTIONS.SETTLE_TRANSCRIBED_VIDEOS: {
            // Only settled error rows: a row still in flight belongs to its import, which settles it itself. The error row also
            // kept RECONCILE_FROM_ARTIFACTS away, so the stale "Not transcribed" outlived the transcript.
            const videos = Array.isArray(action.payload?.videos) ? action.payload.videos : [];
            const videoTranscripts = action.payload?.videoTranscripts;
            let changed = false;
            const files = state.files.map(f => {
                if (f.category !== 'video' || f.status !== FILE_STATUS.ERROR) return f;
                const video = floorRowVideo(videos, f);
                if (!video || !videoHoldsTranscript(video, videoTranscripts)) return f;
                changed = true;
                return { ...f, status: FILE_STATUS.DONE, message: outcomeMessage('transcribed'), outcome: 'transcribed' as ImportOutcome, progress: 100 };
            });
            if (!changed) return state;
            const errors = files.filter(f => f.status === FILE_STATUS.ERROR).length;
            return withLastRun({ ...state, files, errors });
        }

        default:
            return state;
    }
}

export { initialState };
